/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2024 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Götz Görisch, VDW - Verein Deutscher Werkzeugmaschinenfabriken e.V.
 * Copyright 2022-2023 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { Identification, labels } from '@umati/dashboard-common';
import SectionCard from 'components/detail/template/SectionCard';
import _ from 'lodash';
import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import TemplateHelper from 'utilities/TemplateHelper';
import MqttTopics from '../../utilities/MqttTopics';
import SubscriptionComponent from '../../utilities/SubscriptionComponent';
import DetailDivs from '../../views/DetailDivs';
import DetailGallery from '../detail/common/DetailGallery';
import StatusTimeline from '../timeline/StatusTimeline';
import '../../styles/tvViewDetail.scss';
import ENVs from 'utilities/ENVs';

const TvViewDetail = ({ machine, match }) => {
  const [machineDetails, setMachineDetails] = useState({});
  const topic = MqttTopics.Machine(machine.TypeDefinition, machine.MachineId);

  const template = TemplateHelper.getTemplateByTypeId(machine.TypeDefinition);

  const updateMachineViaMqtt = (newMachine) => {
    setMachineDetails(newMachine);
  };

  const getMachineHall = () => {
    return machine?.Positions?.[match.params.fair]?.hall;
  };

  const getMachineBooth = () => {
    return machine?.Positions?.[match.params.fair]?.booth;
  };

  const getHardCodedDetailDiv = (machineDetails, machine) => {
    const identification = machineDetails?.Identification || machine?.Data;
    return (
      <Card className="mb-4">
        <Card.Header>
          <h3>Identification</h3>
        </Card.Header>
        <Card.Body>
          <Identification {...identification} />
        </Card.Body>
      </Card>
    );
  };

  const SupportedBy = function ({ logoSrc, altTxt }) {
    return (
      <div className="col-auto">
        <div className="row logo-container">
          <div className="col">
            <h3>supported by</h3>
          </div>
          <div className="col">
            <img className="logo" src={logoSrc} alt={altTxt} />
          </div>
        </div>
      </div>
    );
  };

  const deviceClassVariable = {
    id: 'ddc4aaca-9595-49c0-b3bc-e39c52f20131',
    modelUri: 'http://opcfoundation.org/UA/Machinery/',
    nodeId: 'ns=1;i=6012',
    displayName: 'DeviceClass',
    variablePath: 'Data.DeviceClass',
    dataType: 'String',
    modellingRule: 'Optional',
    hasProperties: false
  };

  const machineIdentification = machine.Data;
  const model = typeof machineIdentification.Model === 'object' && machineIdentification.Model !== null ? String(machineIdentification.Model.text) : String(machineIdentification.Model);
  const defaultImagePathClassifier = template?.imagesConfig.defaultImagePathClassifierVariable
    ? TemplateHelper.getVariableValue(machineDetails, template.imagesConfig.defaultImagePathClassifierVariable)
    : TemplateHelper.getVariableValue(machine, deviceClassVariable);
  const hall = getMachineHall();
  const booth = getMachineBooth();
  const hasHallAndBooth = hall && booth;
  return (
    <SubscriptionComponent topic={topic} subscriptionCallback={updateMachineViaMqtt} startReceivingData={topic}>
      <div className="view-tvview-detail">
        <div className="container-hero-bg">
        </div>
        <div className="container-hero hero row">
          <div className="col">
            <div className="hero-title">
              <strong>{machineIdentification?.ProductInstanceUri}</strong>
              <h2 className="display-3">{model}</h2>
            </div>
          </div>
          <div className="hall-booth col-auto">
            {hasHallAndBooth && DetailDivs.getHallAndBoothDiv(hall, booth)}
          </div>
        </div>
        <div className="container-gal">
          <DetailGallery imagePath={machine?.ImagePath} machineId={machine?.MachineId} defaultImagePathClassifier={defaultImagePathClassifier} machineType={machine.TypeDefinition} />
        </div>
        <div className="container-status mb-1 mb-md-2 mb-lg-4">
          <h3 className="h5">{labels.statusOverview}</h3>
          <div className="status-timeline">
            <StatusTimeline namespaceUri={machine.MachineId} colorMap={template ? TemplateHelper.getTimelineColorMap(template.timeline) : undefined}/>
          </div>
        </div>
        <div className="container-content auto-columns-2">
          {!template || _.isEmpty(machineDetails)
            ? getHardCodedDetailDiv(machineDetails, machine)
            : template.sections.map((section, sectionIndex) => (
              <>
                {!section.isPlaceholder &&
                  <SectionCard key={TemplateHelper.getKeySection(sectionIndex)} eventKey={TemplateHelper.getKeySection(sectionIndex) + ''}
                    sectionIndex={sectionIndex} sectionDataIndex={0}
                    title={TemplateHelper.getTextValue(section.title)}
                    section={section} data={machineDetails} isExpanded={sectionIndex === 0} />
                }
                {section.isPlaceholder &&
                  <>
                    {TemplateHelper.getPlaceholderObjects(machineDetails, section.placeholderObject).map((data, dataIndex) => (
                      <SectionCard key={TemplateHelper.getKeySection(sectionIndex, dataIndex)} eventKey={TemplateHelper.getKeySection(sectionIndex, dataIndex) + ''}
                        sectionIndex={sectionIndex} sectionDataIndex={dataIndex}
                        title={TemplateHelper.getVariableValue(data, section.titleVariable)}
                        section={section} data={data} isExpanded={sectionIndex === 0 && dataIndex === 0} />
                    ))}
                  </>
                }
              </>
            ))}
        </div>
        <div className="container-footer">
          <div className="row justify-content-around" style={{ paddingTop: '1rem' }}>
            <SupportedBy key="isw" logoSrc={`${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/logo_isw.png`} altTxt="Logo ISW" />
            {/* <SupportedBy key="basys" logoSrc={'/watermarked/TvView/logo_basyskom.png'} altTxt="Logo basyskom" /> */}
            <SupportedBy key="interop4x" logoSrc={`${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/logo_interop4x.png`} altTxt="Logo interop4X" />
          </div>
        </div>
      </div>
    </SubscriptionComponent>
  );
};

export default TvViewDetail;
